import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Phone, MapPin, Calendar, Facebook, Github, Chrome, CheckCircle2, ShieldCheck } from "lucide-react";
import { authService } from "../services/authService";
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from "../firebase";
import toast from "react-hot-toast";

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    dob: "",
    mobile: "",
    address: "",
    pincode: "",
    password: "",
    confirmPassword: "",
    role: "customer",
    avatar: "",
  });

  const [loading, setLoading] = useState(false);

  // Email verification states
  const [emailOtp, setEmailOtp] = useState("");
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  // Mobile verification states
  const [mobileOtp, setMobileOtp] = useState("");
  const [mobileOtpSent, setMobileOtpSent] = useState(false);
  const [mobileOtpLoading, setMobileOtpLoading] = useState(false);
  const [mobileVerified, setMobileVerified] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Helper for Firebase reCAPTCHA
  const setupRegisterRecaptcha = () => {
    if (!window.registerRecaptchaVerifier) {
      window.registerRecaptchaVerifier = new RecaptchaVerifier(auth, 'register-recaptcha', {
        size: 'invisible',
        callback: () => {},
        'expired-callback': () => {
          window.registerRecaptchaVerifier = null;
        }
      });
    }
    return window.registerRecaptchaVerifier;
  };

  // 1. Email OTP Handlers
  const handleSendEmailOtp = async () => {
    if (!formData.email || !/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
      toast.error("Please enter a valid email address first");
      return;
    }

    try {
      setEmailOtpLoading(true);
      const res = await authService.sendEmailOtp(formData.email.trim());
      if (res.success) {
        setEmailOtpSent(true);
        if (res.data?.otp) {
          setEmailOtp(res.data.otp);
          toast.success(`OTP generated: ${res.data.otp} (Add SMTP to .env to receive real emails)`);
        } else {
          toast.success("Verification code sent to your email!");
        }
      } else {
        toast.error(res.message || "Failed to send verification code");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send verification code");
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (!emailOtp || emailOtp.trim().length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }

    try {
      setEmailOtpLoading(true);
      const res = await authService.verifyEmailOtp(formData.email.trim(), emailOtp.trim());
      if (res.success) {
        setEmailVerified(true);
        setEmailOtpSent(false);
        toast.success("Email verified successfully! ✓");
      } else {
        toast.error(res.message || "Invalid verification code");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Invalid verification code");
    } finally {
      setEmailOtpLoading(false);
    }
  };

  // 2. Mobile OTP Handlers (Firebase)
  const handleSendMobileOtp = async () => {
    const cleanDigits = formData.mobile.replace(/\D/g, '');
    if (!cleanDigits || cleanDigits.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number first");
      return;
    }

    try {
      setMobileOtpLoading(true);
      const appVerifier = setupRegisterRecaptcha();
      const formattedMobile = formData.mobile.trim().startsWith('+') ? formData.mobile.trim() : `+91${cleanDigits.slice(-10)}`;

      const confirmation = await signInWithPhoneNumber(auth, formattedMobile, appVerifier);
      setConfirmationResult(confirmation);
      setMobileOtpSent(true);
      toast.success(`OTP sent to ${formattedMobile}`);
    } catch (error) {
      console.error("Firebase SMS error:", error);
      if (window.registerRecaptchaVerifier) {
        try { window.registerRecaptchaVerifier.clear(); } catch (e) {}
        window.registerRecaptchaVerifier = null;
      }
      toast.error(error.message || "Failed to send Mobile OTP");
    } finally {
      setMobileOtpLoading(false);
    }
  };

  const handleVerifyMobileOtp = async () => {
    if (!mobileOtp || mobileOtp.trim().length !== 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }

    if (!confirmationResult) {
      toast.error("Please request an OTP first");
      return;
    }

    try {
      setMobileOtpLoading(true);
      await confirmationResult.confirm(mobileOtp.trim());
      setMobileVerified(true);
      setMobileOtpSent(false);
      toast.success("Mobile number verified successfully! ✓");
    } catch (error) {
      console.error("Mobile OTP verification error:", error);
      toast.error("Invalid mobile OTP. Please try again.");
    } finally {
      setMobileOtpLoading(false);
    }
  };

  // 3. Final Registration Submit
  const handleRegister = async (e) => {
    e.preventDefault();
    const { name, email, mobile, password, confirmPassword } = formData;

    if (!name || !email || !mobile || !password || !confirmPassword) {
      toast.error("All required fields must be filled!");
      return;
    }

    if (!emailVerified) {
      toast.error("Please verify your Email Address with OTP first!");
      return;
    }

    if (!mobileVerified) {
      toast.error("Please verify your Mobile Number with OTP first!");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    const payload = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      phone: formData.mobile,
      avatar: formData.avatar,
      role: formData.role,
      dob: formData.dob,
      pincode: formData.pincode,
      address: formData.address,
    };

    setLoading(true);
    try {
      await authService.register(payload);
      toast.success("Registration successful! Please login with your email and password.");
      navigate("/login");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    toast.success(`Redirecting to ${provider} sign in...`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 p-4 antialiased">
      {/* Invisible reCAPTCHA container for Register Mobile Auth */}
      <div id="register-recaptcha"></div>

      <div className="bg-white/95 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-5 w-full max-w-md animate-fade-slide overflow-hidden">
        
        {/* Header */}
        <div className="text-center mb-4">
          <div className="mx-auto mb-1.5 h-10 w-10 rounded-full bg-white flex items-center justify-center border border-gray-100 shadow-xs overflow-hidden">
            <img 
              src="/images/pronest_logo.png" 
              alt="ProNest Logo" 
              className="h-5 w-5 object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <h2 className="text-base font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            Create Account
          </h2>
          <p className="text-[10px] text-gray-400 font-medium tracking-wide mt-0.5">Join ProNest Marketplace</p>
        </div>

        <form onSubmit={handleRegister} className="space-y-2.5">
          {/* Full Name */}
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
            <input
              type="text"
              name="name"
              placeholder="Full Name *"
              value={formData.name}
              onChange={handleChange}
              className="w-full pl-9 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-purple-500 bg-white"
              required
            />
          </div>

          {/* Email + Verification */}
          <div className="space-y-1">
            <div className="relative flex items-center">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
              <input
                type="email"
                name="email"
                placeholder="Email Address *"
                value={formData.email}
                disabled={emailVerified}
                onChange={handleChange}
                className="w-full pl-9 pr-20 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-purple-500 bg-white disabled:bg-gray-50 disabled:text-gray-500"
                required
              />
              <div className="absolute right-1.5 top-1/2 -translate-y-1/2">
                {emailVerified ? (
                  <span className="flex items-center gap-1 text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded border border-green-200">
                    <CheckCircle2 size={11} /> Verified
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={emailOtpLoading}
                    onClick={handleSendEmailOtp}
                    className="text-[10px] font-semibold bg-purple-50 hover:bg-purple-100 text-purple-700 px-2 py-1 rounded transition-colors disabled:opacity-50"
                  >
                    {emailOtpLoading ? "Sending..." : emailOtpSent ? "Resend" : "Verify Email"}
                  </button>
                )}
              </div>
            </div>

            {/* Email OTP Input Dropdown */}
            {emailOtpSent && !emailVerified && (
              <div className="p-2 bg-purple-50/70 border border-purple-100 rounded-lg flex items-center gap-1.5 animate-fade-in">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={emailOtp}
                  onChange={(e) => setEmailOtp(e.target.value)}
                  placeholder="6-digit Email OTP"
                  className="flex-1 px-2.5 py-1 text-xs border border-purple-200 rounded bg-white font-mono tracking-widest text-center outline-none focus:border-purple-500"
                />
                <button
                  type="button"
                  disabled={emailOtpLoading}
                  onClick={handleVerifyEmailOtp}
                  className="px-2.5 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-[11px] font-semibold transition-colors disabled:opacity-50"
                >
                  {emailOtpLoading ? "..." : "Confirm"}
                </button>
              </div>
            )}
          </div>

          {/* Mobile + Verification */}
          <div className="space-y-1">
            <div className="relative flex items-center">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
              <input
                type="tel"
                name="mobile"
                placeholder="10-digit Mobile *"
                value={formData.mobile}
                disabled={mobileVerified}
                onChange={handleChange}
                className="w-full pl-9 pr-20 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-purple-500 bg-white disabled:bg-gray-50 disabled:text-gray-500"
                required
              />
              <div className="absolute right-1.5 top-1/2 -translate-y-1/2">
                {mobileVerified ? (
                  <span className="flex items-center gap-1 text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded border border-green-200">
                    <CheckCircle2 size={11} /> Verified
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={mobileOtpLoading}
                    onClick={handleSendMobileOtp}
                    className="text-[10px] font-semibold bg-purple-50 hover:bg-purple-100 text-purple-700 px-2 py-1 rounded transition-colors disabled:opacity-50"
                  >
                    {mobileOtpLoading ? "Sending..." : mobileOtpSent ? "Resend" : "Verify Mobile"}
                  </button>
                )}
              </div>
            </div>

            {/* Mobile OTP Input Dropdown */}
            {mobileOtpSent && !mobileVerified && (
              <div className="p-2 bg-purple-50/70 border border-purple-100 rounded-lg flex items-center gap-1.5 animate-fade-in">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={mobileOtp}
                  onChange={(e) => setMobileOtp(e.target.value)}
                  placeholder="6-digit Mobile OTP"
                  className="flex-1 px-2.5 py-1 text-xs border border-purple-200 rounded bg-white font-mono tracking-widest text-center outline-none focus:border-purple-500"
                />
                <button
                  type="button"
                  disabled={mobileOtpLoading}
                  onClick={handleVerifyMobileOtp}
                  className="px-2.5 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-[11px] font-semibold transition-colors disabled:opacity-50"
                >
                  {mobileOtpLoading ? "..." : "Confirm"}
                </button>
              </div>
            )}
          </div>

          {/* Inline Row: DOB & Pincode */}
          <div className="grid grid-cols-2 gap-2">
            <div className="relative">
              <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                className="w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-purple-500 bg-white text-gray-500"
              />
            </div>
            <div className="relative">
              <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                name="pincode"
                placeholder="Pincode"
                value={formData.pincode}
                onChange={handleChange}
                className="w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-purple-500 bg-white"
              />
            </div>
          </div>

          {/* Role Selection */}
          <div className="relative">
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-purple-500 bg-white text-gray-600"
            >
              <option value="customer">Register as Customer</option>
              <option value="vendor">Register as Vendor</option>
            </select>
          </div>

          {/* Address */}
          <div className="relative">
            <MapPin className="absolute left-3 top-2.5 text-gray-400" size={15} />
            <textarea
              name="address"
              placeholder="Full Address"
              value={formData.address}
              onChange={handleChange}
              rows={1.5}
              className="w-full pl-9 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-purple-500 bg-white resize-none"
            />
          </div>

          {/* Passwords Inline Split Grid */}
          <div className="grid grid-cols-2 gap-2">
            <div className="relative">
              <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="password"
                name="password"
                placeholder="Password *"
                value={formData.password}
                onChange={handleChange}
                className="w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-purple-500 bg-white"
                required
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm *"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-purple-500 bg-white"
                required
              />
            </div>
          </div>

          {/* Register Button */}
          <button
            type="submit"
            disabled={loading || !emailVerified || !mobileVerified}
            className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-semibold shadow-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed mt-1"
          >
            {loading ? "Registering..." : !emailVerified || !mobileVerified ? "Verify Email & Mobile to Register" : "Create Account"}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center my-3.5">
          <div className="flex-grow h-px bg-gray-100"></div>
          <span className="px-2.5 text-gray-400 text-[10px] font-medium tracking-wide uppercase">Or Join With</span>
          <div className="flex-grow h-px bg-gray-100"></div>
        </div>

        {/* Social Options Custom Sizing */}
        <div className="grid grid-cols-3 gap-2 text-[11px] font-medium">
          <button onClick={() => handleSocialLogin("Google")} className="py-1.5 border border-gray-200 hover:bg-slate-50 text-gray-600 rounded-lg transition-colors flex items-center justify-center gap-1">
            <Chrome size={13} className="text-red-500" /> Google
          </button>
          <button onClick={() => handleSocialLogin("Facebook")} className="py-1.5 border border-gray-200 hover:bg-slate-50 text-gray-600 rounded-lg transition-colors flex items-center justify-center gap-1">
            <Facebook size={13} className="text-blue-600" /> Facebook
          </button>
          <button onClick={() => handleSocialLogin("GitHub")} className="py-1.5 border border-gray-200 hover:bg-slate-50 text-gray-600 rounded-lg transition-colors flex items-center justify-center gap-1">
            <Github size={13} className="text-gray-800" /> GitHub
          </button>
        </div>

        <p className="text-center text-[11px] text-gray-400 mt-4 font-medium">
          Already have an account?{" "}
          <Link to="/login" className="text-purple-600 hover:text-purple-700 font-semibold transition-colors">
            Login with Password / OTP
          </Link>
        </p>
      </div>

      <style>{`
        .animate-fade-slide {
          animation: registerFadeSlide 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes registerFadeSlide {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-3px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Register;